import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AgentPreferredCitiesUpdateDto, PreferredCityDto } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, tap } from 'rxjs';

@Component({
  selector: 'etxe-agent-cities-form',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './agent-cities-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentCitiesFormComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly destroyRef = inject(DestroyRef);

  readonly preferredCities = input<PreferredCityDto[]>([]);
  readonly selectedDepartments = input<string[]>([]);
  readonly selectedDepartmentsSummary = input<string>('');

  readonly preferredCitiesChange = output<PreferredCityDto[]>();
  readonly validityChanged = output<boolean>();
  readonly dtoChanged = output<{ dto: AgentPreferredCitiesUpdateDto; changed: boolean }>();

  readonly internalPreferredCities = signal<PreferredCityDto[]>([]);
  readonly citySearchQuery = signal<string>('');
  readonly citySuggestions = signal<PreferredCityDto[]>([]);
  readonly isSearchingCity = signal<boolean>(false);
  readonly uncoveredResultsCount = signal<number>(0);

  private readonly citySearchSubject = new Subject<string>();

  constructor() {
    effect(() => {
      const cities = this.preferredCities();
      this.internalPreferredCities.set(cities ? [...cities] : []);
      this.validityChanged.emit(true);
      this.dtoChanged.emit({
        dto: { preferredCities: cities || [] },
        changed: false,
      });
    });
  }

  ngOnInit() {
    this.citySearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => this.isSearchingCity.set(true)),
        switchMap((query: string) => {
          if (!query || query.length < 2) {
            this.isSearchingCity.set(false);
            return of({ features: [] });
          }
          return this.api
            .public()
            .address()
            .search(query, undefined, 'municipality')
            .pipe(catchError(() => of({ features: [] })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res: unknown) => {
        const resObj = res as {
          features?: {
            properties: {
              name?: string;
              postcode?: string;
              citycode?: string;
              city?: string;
              label?: string;
            };
          }[];
        };

        const features = resObj?.features || [];
        const cities: PreferredCityDto[] = [];
        const seen = new Set<string>();
        const depts = this.selectedDepartments();
        const allowedDepts = depts && depts.length > 0 ? new Set(depts) : null;
        let uncovered = 0;

        for (const f of features) {
          const name = f.properties.city || f.properties.name || '';
          const zipCode = f.properties.postcode || '';
          const inseeCode = f.properties.citycode || '';
          const key = `${name.toLowerCase()}-${zipCode}`;
          const deptCode = this.getDepartmentCodeFromZip(zipCode);

          if (name && zipCode) {
            if (!allowedDepts || allowedDepts.has(deptCode)) {
              if (!seen.has(key)) {
                seen.add(key);
                cities.push({
                  name,
                  zipCode,
                  inseeCode,
                  departmentCode: deptCode,
                  label: `${name} (${zipCode})`,
                });
              }
            } else {
              uncovered++;
            }
          }
        }

        this.citySuggestions.set(cities);
        this.uncoveredResultsCount.set(uncovered);
        this.isSearchingCity.set(false);
      });
  }

  onCitySearchInput(value: string): void {
    this.citySearchQuery.set(value);
    this.citySearchSubject.next(value);
  }

  addPreferredCity(city: PreferredCityDto): void {
    const current = this.internalPreferredCities();
    if (current.length >= 5) return;

    const exists = current.some(
      (c) => c.name.toLowerCase() === city.name.toLowerCase() && c.zipCode === city.zipCode,
    );

    if (!exists) {
      const updated = [...current, city];
      this.internalPreferredCities.set(updated);
      this.preferredCitiesChange.emit(updated);
      this.emitState(updated);
    }

    this.citySearchQuery.set('');
    this.citySuggestions.set([]);
    this.uncoveredResultsCount.set(0);
  }

  removePreferredCity(city: PreferredCityDto): void {
    const current = this.internalPreferredCities();
    const updated = current.filter((c) => !(c.name === city.name && c.zipCode === city.zipCode));
    this.internalPreferredCities.set(updated);
    this.preferredCitiesChange.emit(updated);
    this.emitState(updated);
  }

  private emitState(updated: PreferredCityDto[]): void {
    this.validityChanged.emit(true);
    this.dtoChanged.emit({
      dto: { preferredCities: updated },
      changed: true,
    });
  }

  private getDepartmentCodeFromZip(zipCode: string): string {
    if (!zipCode) return '';
    if (zipCode.startsWith('97')) {
      return zipCode.slice(0, 3);
    }
    if (zipCode.startsWith('20')) {
      const num = Number.parseInt(zipCode, 10);
      return num < 20200 ? '2A' : '2B';
    }
    return zipCode.slice(0, 2);
  }
}
